/**
 * Venda Direta: o que precisa sobreviver a uma página recarregada (nesta guia, só nesta
 * sessão do navegador — `sessionStorage`, nunca dados sensíveis nem segredos).
 *
 *  - finalização em andamento: o corpo enviado (com o mesmo `clientRequestId` e o hash da
 *    prévia). Se a página recarregar antes da resposta, a tela oferece "Verificar a venda",
 *    que reenvia o MESMO corpo: a API devolve o pedido já criado (identificador do rascunho
 *    único) ou conclui a finalização que o usuário pediu — nunca um segundo pedido;
 *  - último resultado: a tela de resultado volta depois de recarregar, com a situação da NF-e;
 *  - preferências do "depois da autorização" (abrir o DANFE, baixar PDF e XML): por usuário
 *    neste navegador (`localStorage`).
 *
 * Tudo é por empresa: trocar a empresa ativa ignora o que foi guardado para outra.
 * Sem armazenamento disponível (navegação privada, bloqueio), tudo funciona sem a recuperação.
 */
import type { DirectSaleResult } from "@cafegestor/shared";
import { getActiveCompanyId } from "@/lib/company-store";

/** O resultado da API e o que a tela já sabia (nome do cliente, hora do "Finalizar"). */
export type DirectSaleOutcomeData = DirectSaleResult & {
  customerLabel: string;
  finishedAt: string;
};

const PENDING = "cafegestor.vendaDireta.emAndamento";
const RESULT = "cafegestor.vendaDireta.resultado";
const PREFS = "cafegestor.vendaDireta.automatico";
/** depois disso, o resultado guardado não volta sozinho (a nota segue em Fiscal → Documentos) */
const RESULT_TTL_MS = 6 * 60 * 60 * 1000;
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

type Stored<T> = { companyId: string | null; at: number; value: T };

function read<T>(storage: () => Storage, key: string, ttl: number): T | null {
  try {
    const raw = storage().getItem(key);
    if (!raw) return null;
    const s = JSON.parse(raw) as Stored<T>;
    if (s.companyId !== getActiveCompanyId() || Date.now() - s.at > ttl) return null;
    return s.value;
  } catch {
    return null;
  }
}
function write<T>(storage: () => Storage, key: string, value: T | null) {
  try {
    if (value === null) storage().removeItem(key);
    else
      storage().setItem(
        key,
        JSON.stringify({ companyId: getActiveCompanyId(), at: Date.now(), value }),
      );
  } catch {
    /* sem armazenamento: segue sem a recuperação */
  }
}
const session = () => sessionStorage;
const local = () => localStorage;

/** Corpo da finalização (o mesmo que vai à API). */
export type PendingFinalize = {
  clientRequestId: string;
  customerLabel: string;
  body: Record<string, unknown>;
};

export const pendingFinalize = {
  get: () => read<PendingFinalize>(session, PENDING, PENDING_TTL_MS),
  set: (v: PendingFinalize) => write(session, PENDING, v),
  clear: () => write(session, PENDING, null),
};

export const lastResult = {
  get: () => read<DirectSaleOutcomeData>(session, RESULT, RESULT_TTL_MS),
  set: (v: DirectSaleOutcomeData) => write(session, RESULT, v),
  clear: () => write(session, RESULT, null),
};

export type AutoPrefs = { openDanfe: boolean; download: boolean };
const DEFAULT_PREFS: AutoPrefs = { openDanfe: true, download: true };

export function readAutoPrefs(): AutoPrefs {
  try {
    const raw = localStorage.getItem(PREFS);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw) as Partial<AutoPrefs>;
    return {
      openDanfe: typeof p.openDanfe === "boolean" ? p.openDanfe : DEFAULT_PREFS.openDanfe,
      download: typeof p.download === "boolean" ? p.download : DEFAULT_PREFS.download,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}
export function saveAutoPrefs(p: AutoPrefs): void {
  try {
    local().setItem(PREFS, JSON.stringify(p));
  } catch {
    /* sem armazenamento: vale só nesta tela */
  }
}
